import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { searchTasksQueryOptions } from '@/entities/task'
import { useDebouncedValue } from '@/shared/lib'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { HitRow } from '@/widgets/command-palette'

interface SearchSearch {
  q?: string
  // tag-фильтр (00-architecture § 6): пересечение выдачи с задачами, несущими тег.
  tag?: string
}

export const Route = createFileRoute('/search')({
  component: SearchPage,
  head: () => ({ meta: [{ title: 'Поиск · STIT Task' }] }),
  validateSearch: (s: Record<string, unknown>): SearchSearch => ({
    ...(typeof s.q === 'string' && s.q ? { q: s.q } : {}),
    ...(typeof s.tag === 'string' && s.tag ? { tag: s.tag } : {}),
  }),
})

function SearchPage() {
  const { q = '', tag } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [input, setInput] = useState(q)
  const debounced = useDebouncedValue(input, 300)
  const trimmed = debounced.trim()
  const search = useQuery(searchTasksQueryOptions(trimmed, tag))
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const onChange = (value: string) => {
    setInput(value)
    // Запрос в URL (шарится/переживает перезагрузку); replace — без спама истории.
    // tag сохраняем — фильтр живёт независимо от текстового запроса.
    void navigate({
      search: { ...(value.trim() ? { q: value } : {}), ...(tag ? { tag } : {}) },
      replace: true,
    })
  }

  const clearTag = () => void navigate({ search: trimmed ? { q: trimmed } : {}, replace: true })

  const hits = search.data ?? []
  // Бэк требует непустой запрос (SEARCH_EMPTY_QUERY); тег лишь уточняет выдачу.
  const ready = trimmed.length > 0

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <PageHeader title="Поиск" eyebrow="По задачам, проектам, компаниям и доскам" />

      <div className="mt-6 flex items-center gap-3 rounded-control border border-border-default bg-bg-sunken px-4 focus-within:border-border-accent">
        <Icon name="search" size={18} className="shrink-0 text-text-tertiary" />
        <input
          ref={inputRef}
          type="text"
          aria-label="Поисковый запрос"
          value={input}
          placeholder="Что ищем? Точная фраза — в кавычках, «-» исключает слово"
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent py-3.5 text-text-primary placeholder:text-text-tertiary focus:outline-none"
        />
      </div>

      {tag && (
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-text-tertiary">Фильтр по тегу:</span>
          <Chip variant="soft" className="p-0">
            <button
              type="button"
              onClick={clearTag}
              aria-label={`Снять фильтр по тегу ${tag}`}
              className="flex items-center gap-1.5 px-2.5 py-1 transition-colors hover:text-text-primary"
            >
              {tag}
              <Icon name="close" size={12} />
            </button>
          </Chip>
        </div>
      )}

      <div className="mt-6 max-w-[760px]">
        {!ready ? (
          <p className="px-1 py-8 text-sm text-text-tertiary">
            {tag
              ? `Введите запрос — фильтр по тегу «${tag}» применится к результатам.`
              : 'Введите запрос — поиск по названиям, описаниям и тредам задач.'}
          </p>
        ) : search.isPending ? (
          <p className="px-1 py-8 text-sm text-text-tertiary">Ищем…</p>
        ) : search.isError ? (
          <div className="flex items-center gap-3 px-1 py-8 text-sm text-text-tertiary">
            Поиск недоступен
            <button
              type="button"
              onClick={() => {
                void search.refetch()
              }}
              className="text-text-secondary underline transition-colors hover:text-text-primary"
            >
              Повторить
            </button>
          </div>
        ) : hits.length === 0 ? (
          <EmptyState
            icon={<Icon name="search" size={40} />}
            title="Ничего не нашлось"
            description="Проверьте запрос или попробуйте другие слова."
          />
        ) : (
          <div className="flex flex-col gap-1">
            <p className="px-1 pb-2 text-xs text-text-tertiary">
              Найдено:{' '}
              <span data-numeric className="mono-num">
                {hits.length}
              </span>
            </p>
            {hits.map((hit) => (
              <HitRow key={`${hit.kind}:${hit.id}`} hit={hit} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
